import React, { useState } from 'react';
import { Pill, Plus, Search, Filter, Eye, Edit, Trash2, Package, TrendingUp, AlertCircle } from 'lucide-react';
import { useMedicaments } from '../../hooks/useApi';
import useMedicamentsCrud from '../../hooks/useMedicamentsCrud';
import { useNotification } from '../../hooks/useNotification';
import MedicamentAddModal from './MedicamentAddModal';
import MedicamentEditModal from './MedicamentEditModal';
import MedicamentDetailsModal from './MedicamentDetailsModal';
import MedicamentDeleteModal from './MedicamentDeleteModal';
import Notification from '../common/Notification';

const MedicamentsList = () => {
  const { data: medicaments = [], loading, error, refetch } = useMedicaments();
  const { createMedicament, updateMedicament, deleteMedicament, isLoading } = useMedicamentsCrud();
  const { notification, showNotification, hideNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDosageForm, setFilterDosageForm] = useState('');
  const [filterRequiresPrescription, setFilterRequiresPrescription] = useState('');
  
  const [selectedMedicament, setSelectedMedicament] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Extraire les valeurs uniques pour les filtres
  const categories = [...new Set(medicaments.map(m => m.category))];
  const dosageForms = [...new Set(medicaments.map(m => m.dosageForm))];

  const filteredMedicaments = medicaments.filter(medicament => {
    const matchesSearch = 
      medicament.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicament.genericName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      medicament.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || medicament.category === filterCategory;
    const matchesDosageForm = !filterDosageForm || medicament.dosageForm === filterDosageForm;
    const matchesPrescription = !filterRequiresPrescription || 
      (filterRequiresPrescription === 'true' && medicament.requiresPrescription) ||
      (filterRequiresPrescription === 'false' && !medicament.requiresPrescription);
    
    return matchesSearch && matchesCategory && matchesDosageForm && matchesPrescription;
  });

  const handleAddMedicament = async (formData) => {
    try {
      await createMedicament(formData);
      refetch();
      setShowCreateModal(false);
      showNotification('Médicament ajouté avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      showNotification('Erreur lors de l\'ajout du médicament', 'error');
    }
  };

  const handleEditMedicament = async (medicamentId, formData) => {
    try {
      await updateMedicament(medicamentId, formData);
      refetch();
      setShowEditModal(false);
      setSelectedMedicament(null);
      showNotification('Médicament modifié avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showNotification('Erreur lors de la modification du médicament', 'error');
    }
  };

  const handleDeleteMedicament = async (medicamentId) => {
    try {
      await deleteMedicament(medicamentId);
      refetch();
      setShowDeleteModal(false);
      setSelectedMedicament(null);
      showNotification('Médicament supprimé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showNotification('Erreur lors de la suppression du médicament', 'error');
    }
  };

  const openDetailsModal = (medicament) => {
    setSelectedMedicament(medicament);
    setShowDetailsModal(true);
  };

  const openEditModal = (medicament) => {
    setSelectedMedicament(medicament);
    setShowEditModal(true);
  };

  const openDeleteModal = (medicament) => {
    setSelectedMedicament(medicament);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setShowDeleteModal(false);
    setSelectedMedicament(null);
  };

  const getStockColor = (stock, minStock) => {
    if (stock <= minStock) return 'text-red-600';
    if (stock <= minStock * 2) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Antibiotique': 'bg-blue-100 text-blue-800',
      'Antalgique': 'bg-purple-100 text-purple-800',
      'Anti-inflammatoire': 'bg-indigo-100 text-indigo-800',
      'Antihistaminique': 'bg-cyan-100 text-cyan-800',
      'Antiviral': 'bg-green-100 text-green-800',
      'Antifongique': 'bg-yellow-100 text-yellow-800',
      'Vitamines': 'bg-orange-100 text-orange-800',
      'Autre': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['Autre'];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Médicaments</h1>
              <p className="text-gray-600 mt-2">Gérez et suivez vos médicaments</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span>Nouveau Médicament</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des médicaments...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher un médicament..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <select
                  value={filterDosageForm}
                  onChange={(e) => setFilterDosageForm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les formes</option>
                  {dosageForms.map(form => (
                    <option key={form} value={form}>{form}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              {filteredMedicaments.length} médicament(s) trouvé(s)
            </div>
          </div>
        )}

        {/* Medicaments Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMedicaments.map((medicament) => (
              <div
                key={medicament.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Pill className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{medicament.name}</h3>
                        {medicament.genericName && (
                          <p className="text-sm text-gray-500">{medicament.genericName}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <Package className="h-4 w-4 mr-2" />
                        <span>{medicament.dosageForm} - {medicament.strength}</span>
                      </div>
                      {medicament.manufacturer && (
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="font-medium">Laboratoire:</span>
                          <span className="ml-2">{medicament.manufacturer}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className={`text-sm font-semibold ${getStockColor(medicament.stockQuantity, medicament.minStockLevel)}`}>
                          Stock: {medicament.stockQuantity}
                        </span>
                        {medicament.stockQuantity <= medicament.minStockLevel && (
                          <AlertCircle className="h-4 w-4 text-red-600 ml-2" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(medicament.category)}`}>
                      {medicament.category}
                    </span>
                    {medicament.requiresPrescription && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Prescription requise
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openDetailsModal(medicament)}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Voir les détails"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(medicament)}
                      className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(medicament)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && filteredMedicaments.length === 0 && (
          <div className="text-center py-12">
            <Pill className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun médicament trouvé</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche ou ajoutez un nouveau médicament</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <MedicamentAddModal
        isOpen={showCreateModal}
        onClose={closeModals}
        onSave={handleAddMedicament}
        loading={isLoading}
      />

      <MedicamentEditModal
        medicament={selectedMedicament}
        isOpen={showEditModal}
        onClose={closeModals}
        onSave={handleEditMedicament}
        loading={isLoading}
      />

      <MedicamentDetailsModal
        medicament={selectedMedicament}
        isOpen={showDetailsModal}
        onClose={closeModals}
      />

      <MedicamentDeleteModal
        medicament={selectedMedicament}
        isOpen={showDeleteModal}
        onClose={closeModals}
        onConfirm={handleDeleteMedicament}
        loading={isLoading}
      />

      {/* Notification */}
      <Notification
        isVisible={notification.isVisible}
        onClose={hideNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default MedicamentsList;

